import { differenceInMonths } from 'date-fns'
import { isValidPhoneNumber } from 'react-phone-number-input'
import * as Yup from 'yup'

import { getValidDate } from '@/helpers/date'

// ============================================================================
// VALIDATION MESSAGE KEYS
// ============================================================================

export const REQUIRED = 'validation.required_message'
export const MAX_VALUE = 'validation.max_value'
export const MIN_VALUE = 'validation.min_value'
export const MAX_CHARACTERS = 'validation.max_characters'
export const MIN_CHARACTERS = 'validation.min_characters'
export const DIVIDED_BY_1000 = 'validation.divided_by_1000'
export const MIN_YEAR = 'validation.min_year'
export const MAX_YEAR = 'validation.max_year'

// ============================================================================
// REGEX PATTERNS
// ============================================================================

export const PASSWORD_REGEX = /^(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/
export const SLACK_REGEX = /^@[A-Za-z0-9]+$/
export const WEBSITE_REGEX =
  /^((http(s?)):\/\/)?([wW]{3}.)?(?!.*[wW]{2}.)(?!.*(http(s?)|[wW]{3}.))[a-zA-Z0-9_-]+(\.[a-zA-Z]+)+((\/)[\w#]+)*(\/\w+\?[a-zA-Z0-9_]+=\w+(&[a-zA-Z0-9_]+=\w+)*)?$/g

// ============================================================================
// YUP LOCALE CONFIGURATION
// ============================================================================

Yup.setLocale({
  mixed: {
    required: REQUIRED,
  },
  number: {
    max: ({ max }) => ({ transKey: MAX_VALUE, values: { max } }),
    min: ({ min }) => ({ transKey: MIN_VALUE, values: { min } }),
  },
  array: {
    max: ({ max }) => ({ transKey: MAX_VALUE, values: { max } }),
    min: ({ min }) => ({ transKey: MIN_VALUE, values: { min } }),
  },
  string: {
    email: 'validation.invalid_email',
    max: ({ max }) => ({ transKey: MAX_CHARACTERS, values: { max } }),
    min: ({ min }) => ({ transKey: MIN_CHARACTERS, values: { min } }),
  },
})

// ============================================================================
// CUSTOM YUP METHODS
// ============================================================================

Yup.addMethod(Yup.string, 'phone', function () {
  return this.test('phone', 'validation.phone_not_valid', value => (value ? isValidPhoneNumber(value) : true))
})

Yup.addMethod(Yup.object, 'noNullProperties', function (errorMessage) {
  return this.test('no-null-properties', errorMessage, value => {
    return value && Object.values(value).every(v => v !== null && v !== '')
  })
})

// ============================================================================
// REUSABLE FIELD VALIDATORS
// ============================================================================

export const percent = () => Yup.number().min(0).max(100)
export const days = (max = 365) => Yup.number().min(0).max(max)

// ============================================================================
// CONDITIONAL VALIDATION HELPERS
// ============================================================================

/**
 * Creates a .when() condition that requires the field when the condition is true
 * @example Yup.string().when('fieldName', requiredWhen(value => value === 'something'))
 */
export const requiredWhen = <T>(condition: (value: T) => boolean) => ({
  is: condition,
  then: (schema: Yup.Schema) => schema.required(),
  otherwise: (schema: Yup.Schema) => schema.nullable(),
})

/**
 * Creates a .when() condition that requires the field when the value equals the expected value
 * @example Yup.string().when('fieldName', requiredWhenEquals('expectedValue'))
 */
export const requiredWhenEquals = <T>(expectedValue: T) => ({
  is: expectedValue,
  then: (schema: Yup.Schema) => schema.required(),
  otherwise: (schema: Yup.Schema) => schema.nullable(),
})

/**
 * Creates a .when() condition that requires the field when the value is included in the array
 * @example Yup.string().when('fieldName', requiredWhenIncludes(['value1', 'value2']))
 */
export const requiredWhenIncludes = <T>(values: T[]) => ({
  is: (value: T) => values.includes(value),
  then: (schema: Yup.Schema) => schema.required(),
  otherwise: (schema: Yup.Schema) => schema.nullable(),
})

/** * Creates a .when() condition that requires the field when the condition value is truthy * @example Yup.string().when('fieldName', requiredWhenTruthy()) */
export const requiredWhenTruthy = () => ({
  is: (value: unknown) => !!value,
  then: (schema: Yup.Schema) => schema.required(),
  otherwise: (schema: Yup.Schema) => schema,
})

export const requiredUnlessEquals = <T>(excludedValue: T) => ({
  is: (value: T) => value !== excludedValue,
  then: <S extends Yup.AnySchema>(schema: S) => schema.required(),
  otherwise: <S extends Yup.AnySchema>(schema: S) => schema,
})

// ============================================================================
// VALIDATION HELPER FUNCTIONS
// ============================================================================

export const validateGracePeriod = (issueDate: Date, dueDate: Date, gracePeriod: number) => {
  const validIssueDate = getValidDate(issueDate)
  const validDueDate = getValidDate(dueDate)
  const totalLoanTerm = differenceInMonths(validDueDate, validIssueDate)
  const maxAllowed = isNaN(totalLoanTerm) ? 12 : Math.max(0, Math.min(totalLoanTerm - 1, 12))

  if (gracePeriod > maxAllowed) {
    return {
      isValid: false,
      maxAllowed: maxAllowed,
    }
  }

  return { isValid: true }
}
